import type {
  Plan,
  Subscription,
  SubscriptionStatus,
  SubscriptionStatusAlertInfo,
  SubscriptionStatusAlertStyle,
  SubscriptionStatusPrimaryAction,
  SubscriptionStatusAlertVariant
} from '@/types/subscription'

const NEUTRAL_STYLE: SubscriptionStatusAlertStyle = {
  container: 'bg-gray-50 border border-border',
  iconBg: 'bg-white border border-border',
  icon: 'text-gray-500',
  title: 'text-gray-900',
  text: 'text-muted-foreground'
}

const ALERT_VARIANT_STYLES: Record<SubscriptionStatusAlertVariant, SubscriptionStatusAlertStyle> = {
  warning: NEUTRAL_STYLE,
  danger: NEUTRAL_STYLE,
  muted: NEUTRAL_STYLE
}

interface BaseStatusAlertConfig {
  variant: SubscriptionStatusAlertVariant
  title: string
  body: string
  highlight?: string
  afterHighlight?: string
  secondaryBody?: string
}

function createPaymentRequiredConfig(variant: SubscriptionStatusAlertVariant): BaseStatusAlertConfig {
  return {
    variant,
    title: 'Payment Required',
    body: 'The last payment attempt failed. Update your payment method below, then press ',
    highlight: 'Retry Payment',
    afterHighlight: ' to try the charge again.'
  }
}

// Alert messages for subscription statuses requiring new subscription
function createNewSubscriptionRequiredConfig(variant: SubscriptionStatusAlertVariant): BaseStatusAlertConfig {
  return {
    variant,
    title: 'Tuition Canceled',
    body: 'Tuition is canceled. Please set up a new monthly tuition plan.'
  }
}

const STATUS_ALERT_CONFIG: Partial<Record<SubscriptionStatus, BaseStatusAlertConfig>> = {
  past_due: createPaymentRequiredConfig('warning'),
  unpaid: createPaymentRequiredConfig('danger'),
  incomplete: {
    variant: 'warning',
    title: 'Checkout Incomplete',
    body: 'Your monthly tuition setup is incomplete. Please finish the checkout process to activate your plan.'
  },
  incomplete_expired: createNewSubscriptionRequiredConfig('danger'),
  canceled: createNewSubscriptionRequiredConfig('muted')
}

const STATUS_PRIMARY_ACTIONS: Partial<Record<SubscriptionStatus, SubscriptionStatusPrimaryAction>> = {
  past_due: { label: 'Retry Payment', event: 'retry-payment' },
  unpaid: { label: 'Retry Payment', event: 'retry-payment' },
  incomplete: { label: 'Retry Payment', event: 'retry-payment' },
  incomplete_expired: { label: 'Choose Plan', event: 'subscribe' },
  canceled: { label: 'Choose Plan', event: 'subscribe' }
}

function buildPlanFromSubscription(subscription: Subscription): Plan {
  const amount = parseFloat(subscription.plan_amount ?? '0')
  const formatted = Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`

  return {
    id: typeof subscription.plan === 'number' ? subscription.plan : 0,
    name: subscription.plan_name ?? '',
    description: '',
    stripe_price_id: '',
    amount: subscription.plan_amount ?? 0,
    currency: subscription.plan_currency ?? 'USD',
    interval: subscription.plan_interval ?? 'month',
    is_active: true,
    formatted_amount: formatted
  }
}

export function resolvePlan(subscription?: Subscription | null, assignedPlan?: Plan | null): Plan | null {
  if (subscription?.plan_name) return buildPlanFromSubscription(subscription)

  if (subscription && typeof subscription.plan === 'object' && subscription.plan !== null) {
    return subscription.plan
  }

  return assignedPlan ?? null
}

// Tuition status helpers
export function isTuitionFinished(subscription?: Subscription | null): boolean {
  return subscription?.tuition_status === 'finished'
}

export function getStatusLabel(subscription?: Subscription | null): string {
  if (!subscription) return 'No Tuition'
  if (isTuitionFinished(subscription)) return 'Finished'

  const labels: Record<SubscriptionStatus, string> = {
    active: 'Active',
    trialing: 'Trial',
    past_due: 'Past Due',
    canceled: 'Canceled',
    incomplete: 'Incomplete',
    incomplete_expired: 'Expired',
    unpaid: 'Unpaid'
  }
  return labels[subscription.status] || 'Unknown'
}

export function getStatusColor(subscription?: Subscription | null): string {
  if (!subscription) return 'gray'
  if (isTuitionFinished(subscription)) return 'green'

  const colors: Record<SubscriptionStatus, string> = {
    active: 'green',
    trialing: 'blue',
    past_due: 'yellow',
    canceled: 'gray',
    incomplete: 'orange',
    incomplete_expired: 'red',
    unpaid: 'red'
  }
  return colors[subscription.status] || 'gray'
}

export function formatPlanAmount(plan: Plan | null): string {
  if (!plan) return '—'

  if (plan.formatted_amount) {
    return plan.formatted_amount.replace(/\.00$/, '')
  }

  const amount = Number(plan.amount)
  if (!Number.isFinite(amount)) return '—'

  const currency = (plan.currency ?? 'USD').toString().trim().toUpperCase() || 'USD'
  const formattedAmount = Number.isInteger(amount) ? amount.toString() : amount.toFixed(2)
  return `${currency} ${formattedAmount}`
}

export function resolveIntervalLabel(plan: Plan | null): string {
  return plan?.interval ?? ''
}

export function buildStatusAlert(subscription?: Subscription | null): SubscriptionStatusAlertInfo | null {
  // No alert for finished tuition
  if (isTuitionFinished(subscription)) {
    return null
  }

  const status = subscription?.status ?? null
  const baseConfig = status ? STATUS_ALERT_CONFIG[status] ?? null : null

  const cancellationMessage = subscription?.cancel_at_period_end
    ? buildCancellationMessage(subscription?.current_period_end)
    : null

  if (!baseConfig && !cancellationMessage) {
    return null
  }

  if (!baseConfig) {
    return {
      title: 'Tuition Ending',
      body: cancellationMessage ?? 'Your monthly tuition plan will end at the end of the current period.',
      styles: ALERT_VARIANT_STYLES.danger
    }
  }

  return {
    title: baseConfig.title,
    body: baseConfig.body,
    highlight: baseConfig.highlight,
    afterHighlight: baseConfig.afterHighlight,
    secondaryBody: cancellationMessage ?? baseConfig.secondaryBody,
    styles: ALERT_VARIANT_STYLES[baseConfig.variant]
  }
}

export function resolvePrimaryAction(subscription?: Subscription | null): SubscriptionStatusPrimaryAction | null {
  // No action for finished tuition
  if (isTuitionFinished(subscription)) {
    return null
  }

  if (subscription?.cancel_at_period_end) {
    return {
      label: 'Reactivate Monthly Tuition',
      event: 'reactivate'
    }
  }

  const status = subscription?.status
  if (!status) return null
  return STATUS_PRIMARY_ACTIONS[status] ?? null
}

function buildCancellationMessage(dateString: string | null | undefined): string {
  const formatted = formatDateString(dateString)
  if (!formatted) {
    return 'Your monthly tuition plan will end at the end of the current period.'
  }
  return `Your monthly tuition plan will end on ${formatted}.`
}

function formatDateString(dateString: string | null | undefined): string | null {
  if (!dateString) {
    return null
  }

  const date = new Date(dateString)
  if (isNaN(date.getTime())) {
    return null
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export function formatDate(dateString: string | null | undefined): string {
  return formatDateString(dateString) ?? 'N/A'
}

export function getFreeJobSupportUntil(subscription?: Subscription | null): string | null {
  if (!isTuitionFinished(subscription)) return null
  if (!subscription?.access_expires_at) return null
  return formatDate(subscription.access_expires_at)
}

export function needsPaidJobSupport(subscription?: Subscription | null): boolean {
  return isTuitionFinished(subscription) && !!subscription?.paid_job_support_assigned_at
}
